import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { Confirmation } from '@abp/ng.theme.shared';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ActionMenuComponent } from 'src/app/shared/components/action-menu/action-menu.component';
import { ActionMenuItem } from 'src/app/shared/components/action-menu/models/action-menu-item.model';
import { BaseListComponent } from 'src/app/shared/components/base-list/base-list.component';
import { BaseListColumn } from 'src/app/shared/components/base-list/models/base-list-column.model';
import { BaseListColumnTemplateContext } from 'src/app/shared/components/base-list/models/base-list-column-template-context.model';
import { BaseListConfig } from 'src/app/shared/components/base-list/models/base-list-config.model';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { StateDefinitionCacheService } from 'src/core/services/state-definition/state-definition-cache.service';
import { StateDefinitionDto } from 'src/core/models/state-definition/state-definition.dto';
import { StateType } from 'src/core/models/state-definition/state-type.enum';
import { PredefinedStateName } from 'src/core/constants/predefined-state-name.constant';
import {
  getStatusDotClass,
  getStatusLocalizationKey,
  resolveStatusTone,
} from 'src/core/helpers/agent-status.helper';

type StatusTemplateContext = BaseListColumnTemplateContext<StateDefinitionDto>;

@Component({
  selector: 'app-state-definition-list',
  standalone: true,
  imports: [CommonModule, LocalizationPipe, ActionMenuComponent, BaseListComponent],
  templateUrl: './state-definition-list.component.html',
  styleUrl: './state-definition-list.component.scss',
})
export class StateDefinitionListComponent implements AfterViewInit {
  private readonly localization = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly crudService = inject(CrudService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toasterService = inject(ToasterService);
  private readonly stateCache = inject(StateDefinitionCacheService);

  @ViewChild('nameTpl') nameTpl!: TemplateRef<StatusTemplateContext>;
  @ViewChild('typeTpl') typeTpl!: TemplateRef<StatusTemplateContext>;
  @ViewChild('actionsTpl') actionsTpl!: TemplateRef<StatusTemplateContext>;

  @Input() reloadVersion = 0;

  @Output() edit = new EventEmitter<StateDefinitionDto>();
  @Output() deleted = new EventEmitter<void>();

  statuses: StateDefinitionDto[] = [];
  loading = false;
  listConfig!: BaseListConfig<StateDefinitionDto>;

  ngOnInit(): void {
    this.loadList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadVersion'] && !changes['reloadVersion'].firstChange) {
      this.loadList();
    }
  }

  ngAfterViewInit(): void {
    const l = (key: string) => this.localization.instant(key);

    const columns: BaseListColumn<StateDefinitionDto>[] = [
      {
        field: 'name',
        header: l('Settings::WorkEnvironment:StatusManagement:StatusName'),
        bodyTemplate: this.nameTpl,
      },
      {
        field: 'typeId',
        header: l('Settings::WorkEnvironment:StatusManagement:Type'),
        bodyTemplate: this.typeTpl,
      },
      {
        field: 'id',
        header: l('Settings::WorkEnvironment:StatusManagement:Actions'),
        width: '180px',
        bodyTemplate: this.actionsTpl,
      },
    ];

    this.listConfig = {
      columns,
      trackByField: 'id',
      emptyMessage: l('Settings::WorkEnvironment:StatusManagement:EmptyMessage'),
    };

    this.cdr.detectChanges();
  }

  getTypeLocalizationKey(status: StateDefinitionDto): string {
    const typeName = (StateType as Record<number, string>)[status.typeId];

    return `Settings::WorkEnvironment:StatusManagement:${typeName}`;
  }

  getDisplayName(status: StateDefinitionDto): string {
    if (!status.isPredefined) {
      return status.name;
    }

    const key = getStatusLocalizationKey(status.name);
    const localized = this.localization.instant(key);
    return localized === key ? status.name : localized;
  }

  getColorClass(status: StateDefinitionDto): string {
    return getStatusDotClass(resolveStatusTone(status.name, status.typeId));
  }

  getMenuItems(row: StateDefinitionDto): ActionMenuItem[] {
    if (row.isPredefined) {
      return [];
    }

    return [
      {
        label: this.localization.instant('Settings::WorkEnvironment:StatusManagement:Edit'),
        icon: 'pi pi-pencil',
        action: () => this.onEditClick(row),
      },
      {
        label: this.localization.instant('Settings::WorkEnvironment:StatusManagement:Delete'),
        icon: 'pi pi-trash',
        severity: 'danger',
        action: () => this.onDeleteClick(row),
      },
    ];
  }

  onEditClick(row: StateDefinitionDto): void {
    this.edit.emit(row);
  }

  onDeleteClick(row: StateDefinitionDto): void {
    this.confirmationService
      .warn(
        this.localization.instant(
          'Settings::WorkEnvironment:StatusManagement:DeleteConfirmMessage',
          row.name,
        ),
        'Settings::WorkEnvironment:StatusManagement:DeleteConfirmTitle',
        {
          hideCancelBtn: false,
          hideYesBtn: false,
          dismissible: true,
          yesText: 'Settings::WorkEnvironment:StatusManagement:Delete',
          cancelText: 'AbpUi::Cancel',
        },
      )
      .pipe(
        switchMap(status => {
          if (status === Confirmation.Status.confirm) {
            return this.crudService.delete(StateDefinitionDto, row.id).pipe(map(() => true));
          }

          return of(false);
        }),
      )
      .subscribe({
        next: isDeleted => {
          if (!isDeleted) {
            return;
          }

          this.toasterService.success('::SuccessfullyDeleted');
          this.stateCache.refresh();
          this.deleted.emit();
        },
      });
  }

  private loadList(): void {
    this.loading = true;

    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: 1000,
      filters: [
        {
          field: 'isActive',
          operator: 0,
          value: true,
        },
      ],
      sorters: [],
    };

    this.crudService.get<StateDefinitionDto>(StateDefinitionDto, request).subscribe({
      next: (response: ListResponseDto<StateDefinitionDto>) => {
        this.statuses = this.reorderBeforeCallWork(response.items ?? []);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  /**
   * Moves the predefined BCW (Before Call Work) state to sit directly after the predefined
   * ACW (After Call Work) state, leaving the relative order of all other items unchanged.
   * @param items The state definitions as returned by the backend.
   * @returns A new array with BCW repositioned, or the original array if either state is missing.
   */
  private reorderBeforeCallWork(items: StateDefinitionDto[]): StateDefinitionDto[] {
    const bcwIndex = items.findIndex(item => item.name === PredefinedStateName.Bcw);
    const acwIndex = items.findIndex(item => item.name === PredefinedStateName.Acw);

    if (bcwIndex === -1 || acwIndex === -1 || bcwIndex === acwIndex + 1) {
      return items;
    }

    const reordered = [...items];
    const [bcwItem] = reordered.splice(bcwIndex, 1);
    const insertIndex = bcwIndex < acwIndex ? acwIndex : acwIndex + 1;
    reordered.splice(insertIndex, 0, bcwItem);

    return reordered;
  }
}
